import { ChangeDetectionStrategy, Component, computed, input, OnInit, output, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { FONT_SIZES, FontSize, Theme } from '@psg/domains/shared/services/appearance.service';
import { Popover } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';
import { SidebarItemUiComponent } from '../sidebar-item/sidebar-item.ui-component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [NgOptimizedImage, NgTemplateOutlet, Popover, RouterModule, SidebarItemUiComponent, Tooltip],
  templateUrl: './sidebar.ui-component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarUiComponent implements OnInit {
  readonly isAdmin = input.required<boolean>();
  readonly userName = input.required<string>();
  readonly fontSize = input.required<FontSize>();
  readonly theme = input.required<Theme>();
  readonly isCollapsed = signal<boolean>(false);
  readonly logout = output<void>();
  readonly fontSizeChange = output<FontSize>();
  readonly themeChange = output<Theme>();

  readonly fontSizes = FONT_SIZES;
  readonly fontSizeGlyphs: Record<FontSize, number> = { 10: 12, 12: 15, 14: 18 };
  readonly themes: { value: Theme; label: string; icon: string }[] = [
    { value: 'light', label: 'Light', icon: 'pi pi-sun' },
    { value: 'dark', label: 'Dark', icon: 'pi pi-moon' },
  ];

  readonly initials = computed(() =>
    this.userName()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join(''),
  );

  readonly popoverTokens = computed(() =>
    this.theme() === 'dark'
      ? { root: { background: '#161718', borderColor: '{surface.800}', color: '#ffffff' } }
      : { root: { background: '{cyan.950}', borderColor: '{cyan.900}', color: '#ffffff' } },
  );

  ngOnInit(): void {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState) {
      try {
        const isCollapsed = JSON.parse(savedState);
        this.isCollapsed.set(isCollapsed);
      } catch (error) {
        console.warn('Failed to parse sidebar state from local storage:', error);
      }
    }
  }

  toggleSidebar(): void {
    this.isCollapsed.set(!this.isCollapsed());
    localStorage.setItem('sidebarCollapsed', JSON.stringify(this.isCollapsed()));
  }
}
